import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth } from './AuthContext';
import type { AgentMessageReceivedEvent } from 'lib.store/src/type-partner/ai-agent/models';
import { aiAgentApi } from 'lib.store/src/type-partner/ai-agent/ai-agent.api';
import { ORGANIZATION_TYPE } from 'lib.store/src/main/organization/organizationType';

interface AgentUnreadContextType {
  /** unread agent replies per thread id, cleared when the thread becomes active */
  unreadByThread: Record<number, number>;
  /** register the thread the user is viewing (null on leave) — its replies never count as unread, and registering clears its counter */
  setActiveThreadId: (threadId: number | null) => void;
}

// a permissive default so structure-only consumers (the GenUI rail) render
// fine without the provider — badges are an enhancement, never a crash
const AgentUnreadContext = createContext<AgentUnreadContextType>({
  unreadByThread: {},
  setActiveThreadId: () => {},
});

/**
 * Unread-reply tracking for full-page agent surfaces (the GenUI rail and chat
 * pages) — the same mechanism the Boost.Express assistant sidebar uses in
 * AiAssistantContext: count `.agent.message.received` broadcasts per thread
 * while the user is not viewing that thread, clear on open. Session-scoped,
 * nothing is persisted.
 */
export const AgentUnreadProvider = ({ children }: { children: ReactNode }) => {
  const [unreadByThread, setUnreadByThread] = useState<Record<number, number>>({});
  const activeThreadIdRef = useRef<number | null>(null);

  const { user, isAuthenticated } = useAuth();
  const dispatch = useDispatch();
  const organizationId = user?.organization?.id || '';
  const isAttendee = user?.organization?.type === ORGANIZATION_TYPE.attendee;

  const setActiveThreadId = useCallback((threadId: number | null) => {
    activeThreadIdRef.current = threadId;
    if (threadId === null) return;
    setUnreadByThread((prev) => {
      if (!prev[threadId]) return prev;
      const next = { ...prev };
      delete next[threadId];
      return next;
    });
  }, []);

  useEffect(() => {
    // active only for organizations whose product runs on full-page agent
    // surfaces (attendee today). For the others the floating assistant owns
    // unread counting, and the per-thread invalidations below would refetch
    // whole threads nobody is looking at on every broadcast.
    if (!organizationId || !isAuthenticated || !window.Echo || !isAttendee) return;

    const channel = window.Echo.private(`ai.agent.organization.${organizationId}`);

    const handleMessageReceived = (event: AgentMessageReceivedEvent) => {
      if (event.thread_id !== activeThreadIdRef.current) {
        setUnreadByThread((prev) => ({
          ...prev,
          [event.thread_id]: (prev[event.thread_id] || 0) + 1,
        }));
      }
      // a reply bumps the thread: refresh the list ordering AND stale the
      // thread's cached detail — otherwise reopening its page serves the
      // pre-reply state from cache until a hard refresh
      dispatch(
        aiAgentApi.util.invalidateTags([
          { type: 'ai_agent', id: 'LIST' },
          { type: 'ai_agent', id: event.thread_id },
        ]),
      );
    };

    channel.listen('.agent.message.received', handleMessageReceived);

    return () => {
      // targeted removal — other surfaces share this channel's event
      channel.stopListening('.agent.message.received', handleMessageReceived);
    };
  }, [organizationId, isAuthenticated, isAttendee, dispatch]);

  return (
    <AgentUnreadContext.Provider value={{ unreadByThread, setActiveThreadId }}>
      {children}
    </AgentUnreadContext.Provider>
  );
};

export const useAgentUnread = () => useContext(AgentUnreadContext);
